import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { getFundCentreStatus, getStatusColor, getStatusLabel } from '../utils/timeStatusUtils';
import './FundCentreWorldMap.css';

// Blinking animation keyframes for SVG
const blinkKeyframes = `
  @keyframes blinkMarker {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.15); }
  }
  @keyframes blinkMarkerSelected {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.25); }
  }
`;

// Inject keyframes into document if not already present
if (typeof document !== 'undefined' && !document.getElementById('blink-marker-styles')) {
  const style = document.createElement('style');
  style.id = 'blink-marker-styles';
  style.textContent = blinkKeyframes;
  document.head.appendChild(style);
}

// World map topology (simplified - you can use a more detailed one)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const FundCentreWorldMap = ({ fundCentres, selectedFundCentreId, onMarkerClick, onTableRowClick }) => {
  const [statuses, setStatuses] = useState({});
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [displayedFundCentre, setDisplayedFundCentre] = useState(null);
  const intervalRef = useRef(null);

  // Update statuses for all fund centres
  const updateStatuses = () => {
    const newStatuses = {};
    fundCentres.forEach(fc => {
      if (fc.latitude && fc.longitude) {
        const status = getFundCentreStatus(
          fc.iana_timezone,
          fc.gmt_timezone,
          fc.dst_observed === 'Y',
          9, // Open at 9 AM
          17 // Close at 5 PM
        );
        newStatuses[fc.id] = status;
      }
    });
    setStatuses(newStatuses);
  };

  // Initial status update
  useEffect(() => {
    updateStatuses();
  }, [fundCentres]);

  // Set default fund centre to Colombo on mount
  useEffect(() => {
    if (fundCentres.length > 0 && !displayedFundCentre) {
      const colombo = fundCentres.find(fc => 
        fc.fund_centre_code === 'FC-CMB' || 
        fc.name?.toLowerCase().includes('colombo') ||
        fc.city?.toLowerCase() === 'colombo'
      );
      if (colombo) {
        setDisplayedFundCentre(colombo);
      } else if (fundCentres.length > 0) {
        // If Colombo not found, use first fund centre
        setDisplayedFundCentre(fundCentres[0]);
      }
    }
  }, [fundCentres, displayedFundCentre]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      updateStatuses();
    }, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fundCentres]);

  const handleMarkerMouseEnter = (fc, event) => {
    const status = statuses[fc.id] || getFundCentreStatus(
      fc.iana_timezone,
      fc.gmt_timezone,
      fc.dst_observed === 'Y'
    );
    
    setTooltip({
      fundCentre: fc,
      status: status
    });
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMarkerMouseLeave = () => {
    setTooltip(null);
  };

  const handleMarkerClick = (fc) => {
    setDisplayedFundCentre(fc);
    if (onMarkerClick) {
      onMarkerClick(fc);
    }
  };

  // Filter fund centres that have coordinates - memoized to prevent unnecessary recalculations
  const fundCentresWithCoords = useMemo(() => {
    return fundCentres.filter(fc => {
      const lat = parseFloat(fc.latitude);
      const lng = parseFloat(fc.longitude);
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });
  }, [fundCentres]);

  return (
    <div className="fund-centre-world-map-container">
      <div className="map-header">
        <h3>Fund Centre Status Map</h3>
        <div className="map-legend">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#22c55e' }}></span>
            <span>Open</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#ef4444' }}></span>
            <span>Closed</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#f59e0b' }}></span>
            <span>Opening/Closing Soon</span>
          </div>
        </div>
      </div>

      {/* Fund Centre Details Box */}
      {displayedFundCentre && (() => {
        const status = statuses[displayedFundCentre.id] || getFundCentreStatus(
          displayedFundCentre.iana_timezone,
          displayedFundCentre.gmt_timezone,
          displayedFundCentre.dst_observed === 'Y'
        );
        const color = getStatusColor(status.status);
        
        return (
          <div className="fund-centre-details-box">
            <div className="details-header">
              <h4>{displayedFundCentre.name}</h4>
              <span 
                className="status-indicator" 
                style={{ backgroundColor: color }}
              >
                {getStatusLabel(status.status)}
              </span>
            </div>
            <div className="details-content">
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Fund Centre Code:</span>
                  <span className="detail-value">{displayedFundCentre.fund_centre_code}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">City:</span>
                  <span className="detail-value">{displayedFundCentre.city || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Country:</span>
                  <span className="detail-value">{displayedFundCentre.country}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Currency:</span>
                  <span className="detail-value">{displayedFundCentre.currency}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">GMT Timezone:</span>
                  <span className="detail-value">{displayedFundCentre.gmt_timezone}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">IANA Timezone:</span>
                  <span className="detail-value">{displayedFundCentre.iana_timezone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Local Time:</span>
                  <span className="detail-value">{status.localTime}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">UTC Offset:</span>
                  <span className="detail-value">{status.utcOffset}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">DST Observed:</span>
                  <span className="detail-value">{displayedFundCentre.dst_observed === 'Y' ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Coordinates:</span>
                  <span className="detail-value">
                    {displayedFundCentre.latitude && displayedFundCentre.longitude 
                      ? `${parseFloat(displayedFundCentre.latitude).toFixed(4)}, ${parseFloat(displayedFundCentre.longitude).toFixed(4)}`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
      <div className="map-wrapper">
        <ComposableMap
          projectionConfig={{
            scale: 147,
            center: [0, 20]
          }}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#e5e7eb"
                  stroke="#9ca3af"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none', fill: '#d1d5db' },
                    pressed: { outline: 'none', fill: '#9ca3af' }
                  }}
                />
              ))
            }
          </Geographies>
          
          {fundCentresWithCoords.map(fc => {
            const status = statuses[fc.id] || getFundCentreStatus(
              fc.iana_timezone,
              fc.gmt_timezone,
              fc.dst_observed === 'Y'
            );
            const color = getStatusColor(status.status);
            const isSelected = selectedFundCentreId === fc.id;
            
            // Parse coordinates once and ensure they're numbers
            const lng = typeof fc.longitude === 'number' ? fc.longitude : parseFloat(fc.longitude);
            const lat = typeof fc.latitude === 'number' ? fc.latitude : parseFloat(fc.latitude);
            
            // Skip if coordinates are invalid
            if (isNaN(lng) || isNaN(lat)) {
              return null;
            }
            
            return (
              <Marker
                key={`marker-${fc.id}-${fc.fund_centre_code}`}
                coordinates={[lng, lat]}
                onMouseEnter={(e) => handleMarkerMouseEnter(fc, e)}
                onMouseLeave={handleMarkerMouseLeave}
                onClick={() => handleMarkerClick(fc)}
              >
                <g>
                  <circle
                    r={isSelected ? 10 : 8}
                    fill={color}
                    stroke={isSelected ? '#1e3a8a' : '#ffffff'}
                    strokeWidth={isSelected ? 3 : 2}
                    cx="0"
                    cy="0"
                    style={{
                      cursor: 'pointer',
                      filter: isSelected ? 'drop-shadow(0 0 8px rgba(30, 58, 138, 0.6))' : 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.2))',
                      animation: isSelected ? 'blinkMarkerSelected 1.5s ease-in-out infinite' : 'blinkMarker 2s ease-in-out infinite'
                    }}
                    className="fund-centre-marker"
                  />
                </g>
              </Marker>
            );
          })}
        </ComposableMap>
      </div>

      {tooltip && (
        <div
          className="map-tooltip"
          style={{
            left: `${tooltipPosition.x + 10}px`,
            top: `${tooltipPosition.y + 10}px`
          }}
        >
          <div className="tooltip-header">
            <strong>{tooltip.fundCentre.name}</strong>
          </div>
          <div className="tooltip-content">
            <div className="tooltip-row">
              <span className="tooltip-label">City:</span>
              <span>{tooltip.fundCentre.city || 'N/A'}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Country:</span>
              <span>{tooltip.fundCentre.country}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Local Time:</span>
              <span>{tooltip.status.localTime}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Status:</span>
              <span className={`status-badge status-${tooltip.status.status}`}>
                {getStatusLabel(tooltip.status.status)}
              </span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">UTC Offset:</span>
              <span>{tooltip.status.utcOffset}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Currency:</span>
              <span>{tooltip.fundCentre.currency}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundCentreWorldMap;
